/**
 * Settings → Account & sign-in (design Account board, docs/features/account-sign-in.md): my sign-in emails (add +
 * verify by code, remove, make primary — the old address is emailed a 7-day undo link), my password (set with an
 * email code, change or remove with the current one), passkeys (add, rename, delete, last used) and two-step
 * verification (authenticator app: QR code / typed secret → a code turns it on → 10 backup codes shown once;
 * new backup codes and turning it off need the password, or an email code without one). Rules come from
 * logic/accountSecurity (what can be removed, what proof a change needs, password hints, server error codes).
 *
 * One sheet at a time (`sheet`); every change that lands reloads the account and bumps `saved` (the UI flashes
 * "Saved" next to that group). Online only.
 */
import type { AccountEmail, AccountPasskey, AccountResponse } from "@bundle/contracts";
import { msg, type MessageDescriptor, type MessageKey } from "../i18n.ts";
import {
  canMakePrimary,
  canRemoveEmail,
  canRemoveMethod,
  normalizeCode,
  passwordProblem,
  securityErrorFor,
  signInStateOf,
  sortEmails,
  stepUpFor,
  type PasswordProblem,
  type SignInState,
  type StepUp,
} from "../logic/accountSecurity.ts";
import { ApiFailure, type AccountSecurity, type StepUpProof, type TwoFactorSetup } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";

export type AccountSecurityDeps = {
  security: AccountSecurity;
  /** GET /v1/account (emails, password, passkeys, two-step). */
  account: () => Promise<AccountResponse>;
};

/** The proof a change asks for: the current password, or a code emailed to my primary address. */
export type ProofState = { need: StepUp; password: string; emailCode: string; codeSent: boolean; sending: boolean };

export type AccountSecuritySheet =
  | { kind: "addEmail"; step: "email" | "code"; email: string; emailId?: string; code: string }
  | { kind: "removeEmail"; emailId: string; email: string }
  | { kind: "makePrimary"; emailId: string; email: string; oldEmail: string }
  | { kind: "password"; mode: "set" | "change" | "remove"; next: string; signOutOthers: boolean; problem?: PasswordProblem; proof: ProofState }
  | { kind: "passkeyName"; passkeyId?: string; name: string }
  | { kind: "removePasskey"; passkeyId: string; name: string }
  | { kind: "twoFactorSetup"; step: "proof" | "scan" | "codes"; proof: ProofState; setup?: TwoFactorSetup; code: string }
  | { kind: "twoFactorOff"; proof: ProofState }
  | { kind: "backupCodes"; step: "proof" | "codes"; proof: ProofState; codes?: string[] };

/** Which group just saved (its "Saved" mark flashes). */
export type SavedGroup = "emails" | "password" | "passkeys" | "twoFactor";

export type AccountSecurityState = {
  status: "loading" | "ready" | "error";
  account?: AccountResponse;
  /** Primary, then verified, then waiting for their code. */
  emails: AccountEmail[];
  hasPassword: boolean;
  /** Newest first. */
  passkeys: AccountPasskey[];
  twoFactorEnabled: boolean;
  backupCodesRemaining: number;
  /** Ways in (what can be removed). */
  signIn: SignInState;
  canRemovePassword: boolean;
  canRemovePasskey: boolean;
  sheet?: AccountSecuritySheet;
  /** The open sheet's call is running. */
  busy: boolean;
  /** Why the open sheet's last call failed. */
  error?: MessageDescriptor;
  /** Bumped on each saved change (with the group that changed). */
  saved: { group?: SavedGroup; n: number };
  /** An outcome to tell me about (e.g. the old address was emailed an undo link). */
  info?: MessageDescriptor;
};

const SECURITY_ERRORS = new Set([
  "wrongPassword",
  "locked",
  "invalidCode",
  "challengeExpired",
  "passwordTooShort",
  "passwordTooLong",
  "passwordContainsEmail",
  "passwordTooCommon",
  "passwordLeaked",
  "passwordExists",
  "emailCodeRequired",
  "passwordRequired",
  "lastMethod",
  "emailInUse",
  "invalidEmail",
  "signInAgain",
  "rateLimited",
  "linkExpired",
]);

/** A failed call → the message to show (settings.signIn.error.<SecurityError>, or the generic settings ones). */
export function accountSecurityError(e: unknown): MessageDescriptor {
  if (!(e instanceof ApiFailure)) return msg("settings.failed");
  if (e.kind === "network") return msg("settings.offline");
  const kind = securityErrorFor(e.code);
  if (kind === "failed" && e.kind === "rateLimited") return msg("settings.signIn.error.rateLimited" as MessageKey);
  return SECURITY_ERRORS.has(kind) ? msg(`settings.signIn.error.${kind}` as MessageKey) : msg("settings.failed");
}

/** The browser / OS passkey sheet was closed (not an error). */
function isCancelled(e: unknown): boolean {
  const err = e as { name?: unknown; code?: unknown } | null;
  // "passkey_aborted": the platform runs the ceremony elsewhere (desktop: the system browser).
  return err?.name === "NotAllowedError" || err?.name === "AbortError" || err?.code === "passkey_cancelled" || err?.code === "passkey_aborted";
}

const proofFor = (need: StepUp): ProofState => ({ need, password: "", emailCode: "", codeSent: false, sending: false });

function proofOf(p: ProofState): StepUpProof {
  if (p.need === "password") return { password: p.password };
  if (p.need === "emailCode") return { emailCode: normalizeCode(p.emailCode) };
  return {};
}

function isProofReady(p: ProofState): boolean {
  if (p.need === "password") return p.password !== "";
  if (p.need === "emailCode") return normalizeCode(p.emailCode).length === 6;
  return true;
}

/** A local password problem → its message. */
function problemMessage(p: PasswordProblem): MessageDescriptor {
  const name = { tooShort: "passwordTooShort", tooLong: "passwordTooLong", containsEmail: "passwordContainsEmail", tooCommon: "passwordTooCommon", leaked: "passwordLeaked" }[p];
  return msg(`settings.signIn.error.${name}` as MessageKey);
}

export class AccountSecurityViewModel extends ViewModel<AccountSecurityState> {
  private readonly deps: AccountSecurityDeps;

  constructor(deps: AccountSecurityDeps) {
    super({
      status: "loading",
      emails: [],
      hasPassword: false,
      passkeys: [],
      twoFactorEnabled: false,
      backupCodesRemaining: 0,
      signIn: { emailCode: false, password: false, passkeys: 0, providers: 0 },
      canRemovePassword: false,
      canRemovePasskey: false,
      busy: false,
      saved: { n: 0 },
    });
    this.deps = deps;
  }

  async load(): Promise<void> {
    try {
      const account = await this.deps.account();
      if (!this.isDisposed) this.apply(account);
    } catch {
      if (!this.isDisposed && this.state.account === undefined) this.update({ status: "error" });
    }
  }

  private apply(account: AccountResponse): void {
    const signIn = signInStateOf(account);
    const fallback: AccountEmail[] = account.email === undefined ? [] : [{ id: "primary", email: account.email, primary: true, verified: true, addedAt: 0 }];
    this.update({
      status: "ready",
      account,
      emails: sortEmails(account.emails ?? fallback),
      hasPassword: account.hasPassword === true,
      passkeys: (account.passkeys ?? []).toSorted((a, b) => b.createdAt - a.createdAt),
      twoFactorEnabled: account.twoFactorEnabled === true,
      backupCodesRemaining: account.backupCodesRemaining ?? 0,
      signIn,
      canRemovePassword: canRemoveMethod(signIn, "password"),
      canRemovePasskey: canRemoveMethod(signIn, "passkey"),
    });
  }

  /** My primary address. */
  get primaryEmail(): string {
    return this.state.emails.find((e) => e.primary)?.email ?? this.state.account?.email ?? "";
  }

  private open(sheet: AccountSecuritySheet): void {
    this.update({ sheet, busy: false, error: undefined, info: undefined });
  }

  /** Closes the open sheet (not while its call runs). */
  close(): void {
    if (this.state.busy) return;
    this.update({ sheet: undefined, error: undefined });
  }

  dismissInfo(): void {
    this.update({ info: undefined });
  }

  private patchSheet<K extends AccountSecuritySheet["kind"]>(kind: K, patch: Partial<Extract<AccountSecuritySheet, { kind: K }>>): void {
    const s = this.state.sheet;
    if (s?.kind !== kind) return;
    this.update({ sheet: { ...s, ...patch } as AccountSecuritySheet, error: undefined });
  }

  /** Runs the open sheet's call; on success flashes Saved for `group`, runs `done` and reloads the account. */
  private async run<T>(call: () => Promise<T>, done: (result: T) => void, group: SavedGroup): Promise<void> {
    if (this.state.busy || this.state.sheet === undefined) return;
    this.update({ busy: true, error: undefined });
    let result: T;
    try {
      result = await call();
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, error: isCancelled(e) ? undefined : accountSecurityError(e) });
      return;
    }
    if (this.isDisposed) return;
    this.update({ busy: false, saved: { group, n: this.state.saved.n + 1 } });
    done(result);
    await this.load();
  }

  // ---------- Proof (step-up) ----------

  setProofPassword(password: string): void {
    this.patchProof({ password });
  }

  setProofCode(code: string): void {
    this.patchProof({ emailCode: code.replace(/\D/g, "").slice(0, 6) });
  }

  private patchProof(patch: Partial<ProofState>): void {
    const s = this.state.sheet;
    if (s === undefined || !("proof" in s)) return;
    this.update({ sheet: { ...s, proof: { ...s.proof, ...patch } }, error: undefined });
  }

  /** Emails the step-up code to my primary address (again: a new one). */
  async sendProofCode(): Promise<void> {
    const s = this.state.sheet;
    if (s === undefined || !("proof" in s) || s.proof.need !== "emailCode" || s.proof.sending) return;
    this.patchProof({ sending: true });
    try {
      await this.deps.security.sendStepUpCode();
      this.patchProof({ sending: false, codeSent: true });
    } catch (e) {
      this.patchProof({ sending: false });
      this.update({ error: accountSecurityError(e) });
    }
  }

  /** The open sheet's proof is filled in. */
  get proofReady(): boolean {
    const s = this.state.sheet;
    return s !== undefined && "proof" in s && isProofReady(s.proof);
  }

  // ---------- Emails ----------

  startAddEmail(): void {
    this.open({ kind: "addEmail", step: "email", email: "", code: "" });
  }

  setNewEmail(email: string): void {
    this.patchSheet("addEmail", { email });
  }

  setEmailCode(code: string): void {
    this.patchSheet("addEmail", { code: code.replace(/\D/g, "").slice(0, 6) });
  }

  /** Adds the address and emails it a code (again on the code step: resends). */
  async sendEmailCode(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "addEmail" || s.email.trim() === "" || this.state.busy) return;
    this.update({ busy: true, error: undefined });
    try {
      const row = await this.deps.security.addEmail(s.email.trim());
      if (this.isDisposed) return;
      this.update({ busy: false });
      this.patchSheet("addEmail", { step: "code", emailId: row.id, email: row.email, code: "" });
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, error: accountSecurityError(e) });
    }
  }

  /** Back to the address (a different one). */
  editNewEmail(): void {
    this.patchSheet("addEmail", { step: "email", code: "" });
  }

  async verifyEmail(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "addEmail" || s.step !== "code" || s.emailId === undefined) return;
    const id = s.emailId;
    const code = normalizeCode(s.code);
    if (code.length !== 6) {
      this.update({ error: msg("settings.signIn.error.invalidCode" as MessageKey) });
      return;
    }
    await this.run(() => this.deps.security.verifyEmail(id, code), () => this.update({ sheet: undefined }), "emails");
  }

  /** "Verify" on an address still waiting for its code: sends a new one and opens the code step. */
  async resumeVerify(emailId: string): Promise<void> {
    const row = this.state.emails.find((e) => e.id === emailId);
    if (row === undefined || row.verified) return;
    this.open({ kind: "addEmail", step: "email", email: row.email, code: "" });
    await this.sendEmailCode();
  }

  canRemoveEmail(emailId: string): boolean {
    return canRemoveEmail(this.state.emails, emailId);
  }

  askRemoveEmail(emailId: string): void {
    const row = this.state.emails.find((e) => e.id === emailId);
    if (row === undefined || !canRemoveEmail(this.state.emails, emailId)) return;
    this.open({ kind: "removeEmail", emailId, email: row.email });
  }

  async removeEmail(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "removeEmail") return;
    await this.run(() => this.deps.security.removeEmail(s.emailId), () => this.update({ sheet: undefined }), "emails");
  }

  canMakePrimary(emailId: string): boolean {
    return canMakePrimary(this.state.emails, emailId);
  }

  askMakePrimary(emailId: string): void {
    const row = this.state.emails.find((e) => e.id === emailId);
    if (row === undefined || !canMakePrimary(this.state.emails, emailId)) return;
    this.open({ kind: "makePrimary", emailId, email: row.email, oldEmail: this.primaryEmail });
  }

  async makePrimary(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "makePrimary") return;
    await this.run(
      () => this.deps.security.makePrimary(s.emailId),
      () => this.update({ sheet: undefined, info: msg("settings.signIn.emails.primaryChanged" as MessageKey, { email: s.email, old: s.oldEmail }) }),
      "emails",
    );
  }

  // ---------- Password ----------

  /** Set (no password yet: an email code proves it), change (the current one), or remove (the current one). */
  startPassword(mode: "set" | "change" | "remove"): void {
    if (mode === "remove" && !this.state.canRemovePassword) return;
    const m = mode === "remove" ? "remove" : this.state.hasPassword ? "change" : "set";
    const need = stepUpFor(m === "set" ? "setPassword" : m === "change" ? "changePassword" : "removePassword", this.state.hasPassword);
    this.open({ kind: "password", mode: m, next: "", signOutOthers: true, problem: "tooShort", proof: proofFor(need) });
  }

  setNewPassword(next: string): void {
    const problem = passwordProblem(next, this.primaryEmail);
    const s = this.state.sheet;
    if (s?.kind !== "password") return;
    const sheet = { ...s, next };
    if (problem === undefined) delete sheet.problem;
    else sheet.problem = problem;
    this.update({ sheet, error: undefined });
  }

  setSignOutOthers(on: boolean): void {
    this.patchSheet("password", { signOutOthers: on });
  }

  /** Saves (or removes) the password. */
  async savePassword(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "password" || !isProofReady(s.proof)) return;
    if (s.mode === "remove") {
      await this.run(() => this.deps.security.removePassword(s.proof.password), () => this.update({ sheet: undefined }), "password");
      return;
    }
    if (s.problem !== undefined) {
      this.update({ error: problemMessage(s.problem) });
      return;
    }
    const req = { newPassword: s.next, signOutOthers: s.signOutOthers, ...(s.proof.need === "password" ? { currentPassword: s.proof.password } : { emailCode: normalizeCode(s.proof.emailCode) }) };
    await this.run(
      () => this.deps.security.setPassword(req),
      (signedOut) => this.update({ sheet: undefined, info: signedOut > 0 ? msg("settings.signIn.password.signedOut" as MessageKey, { count: signedOut }) : undefined }),
      "password",
    );
  }

  // ---------- Passkeys ----------

  startAddPasskey(defaultName = ""): void {
    this.open({ kind: "passkeyName", name: defaultName });
  }

  startRenamePasskey(passkeyId: string): void {
    const pk = this.state.passkeys.find((p) => p.id === passkeyId);
    if (pk !== undefined) this.open({ kind: "passkeyName", passkeyId, name: pk.name ?? "" });
  }

  setPasskeyName(name: string): void {
    this.patchSheet("passkeyName", { name });
  }

  /** Add: runs the browser / OS passkey sheet and saves it (closing that sheet is not an error). Rename: saves the name. */
  async savePasskey(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "passkeyName") return;
    const name = s.name.trim();
    if (s.passkeyId !== undefined) {
      if (name === "") return;
      const id = s.passkeyId;
      await this.run(() => this.deps.security.renamePasskey(id, name), () => this.update({ sheet: undefined }), "passkeys");
      return;
    }
    await this.run(() => this.deps.security.addPasskey(name === "" ? undefined : name), () => this.update({ sheet: undefined }), "passkeys");
  }

  askRemovePasskey(passkeyId: string): void {
    const pk = this.state.passkeys.find((p) => p.id === passkeyId);
    if (pk === undefined || !this.state.canRemovePasskey) return;
    this.open({ kind: "removePasskey", passkeyId, name: pk.name ?? "" });
  }

  async removePasskey(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "removePasskey") return;
    await this.run(() => this.deps.security.removePasskey(s.passkeyId), () => this.update({ sheet: undefined }), "passkeys");
  }

  // ---------- Two-step verification ----------

  startTwoFactor(): void {
    if (this.state.twoFactorEnabled) return;
    this.open({ kind: "twoFactorSetup", step: "proof", proof: proofFor(stepUpFor("enableTwoFactor", this.state.hasPassword)), code: "" });
  }

  /** Setup step 1 → the QR code and secret. */
  async continueTwoFactor(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "twoFactorSetup" || s.step !== "proof" || !isProofReady(s.proof) || this.state.busy) return;
    this.update({ busy: true, error: undefined });
    try {
      const setup = await this.deps.security.startTwoFactor(proofOf(s.proof));
      if (this.isDisposed) return;
      this.update({ busy: false });
      this.patchSheet("twoFactorSetup", { step: "scan", setup, code: "" });
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, error: accountSecurityError(e) });
    }
  }

  setTwoFactorCode(code: string): void {
    this.patchSheet("twoFactorSetup", { code: code.replace(/\D/g, "").slice(0, 6) });
  }

  /** Step 2: the authenticator's code turns it on; then the backup codes (shown once). */
  async confirmTwoFactor(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "twoFactorSetup" || s.step !== "scan") return;
    if (s.code.length !== 6) {
      this.update({ error: msg("settings.signIn.error.invalidCode" as MessageKey) });
      return;
    }
    const code = s.code;
    await this.run(() => this.deps.security.confirmTwoFactor(code), () => this.patchSheet("twoFactorSetup", { step: "codes" }), "twoFactor");
  }

  startTwoFactorOff(): void {
    if (!this.state.twoFactorEnabled) return;
    this.open({ kind: "twoFactorOff", proof: proofFor(stepUpFor("disableTwoFactor", this.state.hasPassword)) });
  }

  async turnOffTwoFactor(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "twoFactorOff" || !isProofReady(s.proof)) return;
    await this.run(() => this.deps.security.disableTwoFactor(proofOf(s.proof)), () => this.update({ sheet: undefined }), "twoFactor");
  }

  startNewBackupCodes(): void {
    if (!this.state.twoFactorEnabled) return;
    this.open({ kind: "backupCodes", step: "proof", proof: proofFor(stepUpFor("regenerateRecoveryCodes", this.state.hasPassword)) });
  }

  async regenerateBackupCodes(): Promise<void> {
    const s = this.state.sheet;
    if (s?.kind !== "backupCodes" || s.step !== "proof" || !isProofReady(s.proof)) return;
    await this.run(() => this.deps.security.regenerateBackupCodes(proofOf(s.proof)), (codes) => this.patchSheet("backupCodes", { step: "codes", codes }), "twoFactor");
  }

  /** The backup codes on screen (setup's last step or new ones), for copy / download. */
  get shownBackupCodes(): string[] | undefined {
    const s = this.state.sheet;
    if (s?.kind === "twoFactorSetup" && s.step === "codes") return s.setup?.backupCodes;
    if (s?.kind === "backupCodes" && s.step === "codes") return s.codes;
    return undefined;
  }
}

/** Backup codes as a .txt file (download): a heading, the date, one code per line. */
export function backupCodesFile(codes: string[], email: string, heading: string): { name: string; text: string } {
  return { name: "bundle-backup-codes.txt", text: `${heading}\n${email}\n\n${codes.join("\n")}\n` };
}
